import {
  ACC_TYPE_INACTIVE,
  ACC_TYPE_INVITED,
  accountTypeKeys,
  accountTypes,
  userPermissions,
  userStateType,
} from '../enum/api/accountTypes';
import { parseEnumType } from './enumMappers';
import { joinArrayWithComma, separateByUnderscore } from './stringHelpers';
import i18next from '../../i18n';
import { USERNAMES } from '../../constants/sessionStorage';
import { SUFFIX_TYPES_ARRAY } from '../../constants/registrationConstants';
import { retrieveFromSessionStorage } from './sessionStorageHelper';

export const formatUsersTableRowData = (
  users,
  setUserToRemove,
  editPermission,
  resendInvite,
  activateAccount,
  currentUser,
  isRemoveButtonAllowedForCurrentUser,
  isImpersonating,
  allowAccountChangesPermission,
  displayBasicAccountOptionsPermission,
) =>
  users.map((user) => {
    const activateAccountAction = () => {
      activateAccount(user);
    };
    const editPermissions = () => {
      editPermission(user);
    };
    const removeAccount = () => {
      setUserToRemove(user.UserUid);
    };

    const resendInviteAction = () => {
      resendInvite(user);
    };

    let actions = [
      {
        id: `${user.UserUid}-edit-action`,
        label: i18next.t('userManagement.actionBtns.editPermissions'),
        action: editPermissions,
        variant: 'primaryOutlined',
        size: 'sm',
        textTransform: 'capitalize',
        disabled:
          !allowAccountChangesPermission ||
          displayBasicAccountOptionsPermission,
      },
      {
        id: `${user.UserUid}-remove-action`,
        label: i18next.t('common.remove'),
        action: removeAccount,
        variant: 'primaryClear',
        size: 'sm',
        textTransform: 'capitalize',
        minWidth: 'auto',
        disabled:
          !allowAccountChangesPermission ||
          displayBasicAccountOptionsPermission,
        hidden:
          currentUser?.UserUid === user?.UserUid &&
          isRemoveButtonAllowedForCurrentUser,
      },
    ];

    if (
      parseEnumType(userStateType, user.StateType) === ACC_TYPE_INACTIVE &&
      isImpersonating
    ) {
      actions = [
        {
          id: `${user.UserUid}-activate-action`,
          label: i18next.t('userManagement.activate.title'),
          action: activateAccountAction,
          variant: 'primary',
          size: 'sm',
          textTransform: 'capitalize',
          disabled:
            !allowAccountChangesPermission ||
            displayBasicAccountOptionsPermission,
        },

        ...actions,
      ];
    }
    if (
      parseEnumType(userStateType, user.StateType) === ACC_TYPE_INVITED &&
      user.RoleType !== 1
    ) {
      actions = [
        {
          id: `${user.UserUid}-invite-action`,
          label: i18next.t('userManagement.resendInvite.title'),
          action: resendInviteAction,
          variant: 'primary',
          size: 'sm',
          textTransform: 'capitalize',
          disabled:
            !allowAccountChangesPermission ||
            displayBasicAccountOptionsPermission,
        },
        ...actions,
      ];
    }
    return {
      id: user.UserUid,
      user,
      content: user.content,
      actions,
    };
  });

export const formatUsersTableHeaderData = () => [
  { id: 1, content: i18next.t('userManagement.tableHeader.users') },
  { id: 2, content: i18next.t('userManagement.tableHeader.email') },
  { id: 3, content: i18next.t('userManagement.tableHeader.permissions') },
  { id: 4, content: i18next.t('userManagement.tableHeader.status') },
  { id: 5, content: '' },
];

export function formatUserPermissions(data) {
  const permissionsEnumerated = data.Permissions.map(
    (perm) => userPermissions[perm],
  );
  return separateByUnderscore(joinArrayWithComma(permissionsEnumerated));
}

export function formatUsernamesSelect(usernames) {
  return usernames.map((username) => ({ label: username, value: username }));
}

export function getUsernames() {
  const usernames = retrieveFromSessionStorage(USERNAMES);
  const usernameOptions = usernames
    ? usernames.map((username) => ({
        label: username,
        value: username,
        dataCy: username,
      }))
    : [];

  return usernameOptions;
}

const isNameSuffix = (word) =>
  SUFFIX_TYPES_ARRAY.some(
    ({ value, label }) => value !== '' && label === word.toUpperCase(),
  );

// UTMA accounts are named "For the benefit of <minor's name>"; the initials
// belong to the minor, so the lead-in is not part of the name.
const BENEFIT_OF_PREFIX = /^\s*for the benefit of\b\s*/i;

// Joint accounts are named "John Doe and Jane Doe", primary owner first.
const JOINT_OWNER_SEPARATOR = /\s+and\s+/i;

const isJointAccount = ({ AccountType }) => {
  const type = parseEnumType(accountTypeKeys, AccountType);
  return (
    type === accountTypes.JointTenantsWithRightOfSurvivorship ||
    type === accountTypes.JointTenantsInCommon
  );
};

// The account types registered under the Corporate application type.
const BUSINESS_ACCOUNT_TYPES = [
  accountTypes.Corporate,
  accountTypes.Partnership,
  accountTypes.SoleProprietorship,
  accountTypes.LimitedLiabilityCorporation,
  accountTypes.Trust,
];

const isBusinessAccount = ({ AccountType }) =>
  BUSINESS_ACCOUNT_TYPES.includes(parseEnumType(accountTypeKeys, AccountType));

// "John Doe, III" -> ["John", "Doe", "III"]
const getNameWords = (name) =>
  name
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);

// "John Doe III (GS-241107-010-120)" -> "JD". Built from the account's
// DisplayName; the account code is stripped like the rest of the account UI.
// Joint accounts take the first initial of each owner's first name instead:
// "John Doe and Jane Doe" -> "JJ". Business accounts take the first initial of
// the first two words of the name: "Acme Gold Holdings LLC" -> "AG".
export function getAccountInitials(account) {
  if (!account || account.AccountType === 0) {
    return '';
  }

  const name = String(account.DisplayName ?? '')
    .replace(`(${account.AccountCode})`, '')
    .replace(BENEFIT_OF_PREFIX, '');

  if (isJointAccount(account)) {
    const [primary, secondary] = name
      .split(JOINT_OWNER_SEPARATOR)
      .map(getNameWords)
      .filter((owner) => owner.length);

    if (primary && secondary) {
      return `${primary[0][0]}${secondary[0][0]}`.toUpperCase();
    }
  }

  const words = getNameWords(name);

  if (isBusinessAccount(account)) {
    return words
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  while (words.length > 1 && isNameSuffix(words[words.length - 1])) {
    words.pop();
  }

  if (!words.length) {
    return '';
  }

  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';

  return `${first}${last}`.toUpperCase();
}
